// GIVE-GET REFERRAL LINKS (blueprint section 04, 2026-10-16).
//
// Any page opened with ?ref=<code> remembers the code for 30 days, so a friend
// who looks around first and signs up later still brings it. Sign-up (password
// or Google, lib/api.ts) sends it; the server gives the gift once
// (services/referrals.js). The latest link wins until an account is made.

const KEY = 'fs_ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[a-z0-9]{6,16}$/;

export function cleanReferralCode(raw: unknown): string | null {
  const code = String(raw ?? '').trim().toLowerCase();
  return CODE_RE.test(code) ? code : null;
}

/** Reads ?ref= from a URL search string and keeps it. Returns the code kept, if any. */
export function captureReferral(search: string = typeof window !== 'undefined' ? window.location.search : ''): string | null {
  let code: string | null = null;
  try {
    code = cleanReferralCode(new URLSearchParams(search).get('ref'));
  } catch {
    return null;
  }
  if (!code) return null;
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    /* private mode: the code still rides this visit's URL */
  }
  return code;
}

export function readReferralCode(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { code?: string; at?: number };
    const code = cleanReferralCode(saved.code);
    if (!code || typeof saved.at !== 'number' || now - saved.at > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/** The share link for a stage entry path, carrying the sharer's code when they have one. */
export function referralLink(origin: string, path: string, code?: string | null): string {
  const url = new URL(path, origin);
  if (code) url.searchParams.set('ref', code);
  return url.toString();
}
